
const OVERLAP_BUFFER_PX = 50;
/** Pixels past the geometric “tabs meet section bottom” line before treating as section end. */
const SECTION_END_BUFFER_PX = 2;
/** Hide sticky tabs this many viewport pixels before the section bottom reaches the tabs bottom. */
const SECTION_END_EARLY_PX = 100;
/** Viewport Y from top; panel tops at or above this line select the matching tab (scrollspy). */
const SCROLL_SPY_ACTIVATION_BUFFER_PX = 1;
const DATA_TABS_LAYOUT_HEIGHT = "data-tabs-layout-height";

/**
 * Bar height used for scroll alignment and scrollspy (stable when tabs are display:none).
 */
function getTabsBarOffsetPx(section) {
  const tabsEl = section.querySelector(".tabs-component");
  if (!tabsEl) {
    return 0;
  }
  const h = tabsEl.getBoundingClientRect().height;
  if (h > 1) {
    tabsEl.setAttribute(DATA_TABS_LAYOUT_HEIGHT, String(Math.round(h)));
    return h;
  }
  const cached = Number.parseInt(tabsEl.getAttribute(DATA_TABS_LAYOUT_HEIGHT) ?? "", 10);
  return Number.isFinite(cached) && cached > 0 ? cached : 0;
}

function setActiveTabId(section, tabId) {
  if (!tabId) {
    return;
  }
  const listItems = section.querySelectorAll(".contact-tabs .tabs-component__header-list li");
  listItems.forEach((li) => {
    const link = li.querySelector('a[href^="#"]');
    const href = link?.getAttribute("href");
    const id = href && href.length >= 2 ? href.slice(1) : "";
    li.classList.toggle("active", id === tabId);
  });
}

/**
 * Activates the tab whose `[data-tab]` panel last crossed the sticky offset (same rule as tab clicks).
 */
function updateActiveTabFromScroll(section) {
  const sectionRect = section.getBoundingClientRect();
  if (sectionRect.top > window.innerHeight) {
    return;
  }

  const panels = section.querySelectorAll(".contact-tabs__content-item[data-tab]");
  if (!panels.length) {
    return;
  }

  const offsetPx = getTabsBarOffsetPx(section);
  let activeTabId = null;

  for (let i = panels.length - 1; i >= 0; i--) {
    const panel = panels[i];
    const top = panel.getBoundingClientRect().top;
    if (top <= offsetPx + SCROLL_SPY_ACTIVATION_BUFFER_PX) {
      activeTabId = panel.getAttribute("data-tab");
      break;
    }
  }

  if (!activeTabId) {
    activeTabId = panels[0].getAttribute("data-tab");
  }

  setActiveTabId(section, activeTabId);
}

function getTabsBottomViewportY(tabsEl, tabsRect) {
  if (tabsRect.height > 1) {
    tabsEl.setAttribute(DATA_TABS_LAYOUT_HEIGHT, String(Math.round(tabsRect.height)));
    return tabsRect.bottom;
  }
  const cached = Number.parseInt(tabsEl.getAttribute(DATA_TABS_LAYOUT_HEIGHT) ?? "", 10);
  return Number.isFinite(cached) && cached > 0 ? cached : 0;
}

function updateStickyTabsVisibility(section) {
  const tabsEl = section.querySelector(".tabs-component");
  const prevSection = section.previousElementSibling;
  if (!tabsEl) {
    return;
  }

  const sectionRect = section.getBoundingClientRect();
  const tabsRect = tabsEl.getBoundingClientRect();
  const stickyEngaged = sectionRect.top < 0;

  let overlapsPrevSection = false;
  if (prevSection instanceof HTMLElement) {
    const prevRect = prevSection.getBoundingClientRect();
    overlapsPrevSection =
      prevRect.bottom > OVERLAP_BUFFER_PX &&
      prevRect.bottom > tabsRect.top &&
      prevRect.top < tabsRect.bottom;
  }

  const tabsBottomY = getTabsBottomViewportY(tabsEl, tabsRect);
  const atSectionEnd =
    stickyEngaged &&
    tabsBottomY > 0 &&
    sectionRect.bottom <=
      tabsBottomY + SECTION_END_BUFFER_PX + SECTION_END_EARLY_PX;

  const hideForOverlap = stickyEngaged && overlapsPrevSection;
  const hideTabs = hideForOverlap || atSectionEnd;

  tabsEl.style.opacity = hideTabs ? "0" : "1";
  tabsEl.style.visibility = hideTabs ? "hidden" : "visible";
  tabsEl.classList.toggle("tabs-component--at-section-end", atSectionEnd);
  tabsEl.classList.toggle("stickyTabs", stickyEngaged && !hideTabs);
}

function scrollElementIntoViewWithOffset(element, offset) {
  const rect = element.getBoundingClientRect();
  const top = rect.top + window.scrollY - offset;
  window.scrollTo({
    top: Math.max(0, top),
    behavior: "smooth",
  });
}

export function initContactTabs() {
  const sections = document.querySelectorAll(".contact-tabs");
  if (!sections.length) return;

  let scheduled = false;
  const scheduleVisibilityUpdate = () => {
    if (scheduled) {
      return;
    }
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      sections.forEach((section) => {
        updateStickyTabsVisibility(section);
        updateActiveTabFromScroll(section);
      });
    });
  };

  window.addEventListener("scroll", scheduleVisibilityUpdate, { passive: true });
  window.addEventListener("resize", scheduleVisibilityUpdate);
  scheduleVisibilityUpdate();

  sections.forEach((section) => {
    const tabLinks = section.querySelectorAll(
      ".contact-tabs .tabs-component__header-list a[href^='#']"
    );

    tabLinks.forEach((link) => {
      link.addEventListener("click", (event) => {
        const href = link.getAttribute("href");
        if (!href || href.length < 2) return;

        const tabId = href.slice(1);
        const target = section.querySelector(`[data-tab="${CSS.escape(tabId)}"]`);
        if (!target) return;

        event.preventDefault();

        const offset = getTabsBarOffsetPx(section);
        scrollElementIntoViewWithOffset(target, offset);

        setActiveTabId(section, tabId);
      });
    });
  });




}
